import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { DocumentMetadataSection } from './DocumentMetadataSection'
import { OcrTable } from './OcrTable'
import { SubjectDocumentsPanel } from './SubjectDocumentsPanel'

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
  border-left: 0.0625rem solid ${(props) => props.theme.forecast.border};
  height: 100%;
`

const SidebarContent = styled.div<{ $isCollapsed: boolean }>`
  flex: 1;
  min-height: 0;
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'flex')};
  flex-direction: column;
  overflow: hidden;
`

export type KnowledgeDocumentSidebarProps = {
  handleResetZoom: () => void
  handleZoomToBox: (id: string) => void
  /** Subject whose documents are shown at the top of the sidebar (set by
   * clicking an alias box in the viewer); null hides the panel. */
  selectedSubjectUid?: string | null
  clearSelectedSubject?: () => void
}

export function KnowledgeDocumentSidebar({
  handleResetZoom,
  handleZoomToBox,
  selectedSubjectUid,
  clearSelectedSubject,
}: KnowledgeDocumentSidebarProps) {
  const { isSidebarCollapsed } = useKnowledgeDocumentUXContext()
  const previousCollapsedState = useRef(isSidebarCollapsed)
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (previousCollapsedState.current === isSidebarCollapsed) {
      return
    }

    previousCollapsedState.current = isSidebarCollapsed
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
    }

    resetTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleResetZoom()
        })
      })
    }, 350)

    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [isSidebarCollapsed, handleResetZoom])

  return (
    <Sidebar>
      <SidebarContent $isCollapsed={isSidebarCollapsed}>
        {selectedSubjectUid && (
          <SubjectDocumentsPanel
            subjectUid={selectedSubjectUid}
            onClose={() => clearSelectedSubject?.()}
          />
        )}
        <OcrTable handleZoomToBox={handleZoomToBox} />
        <DocumentMetadataSection />
      </SidebarContent>
    </Sidebar>
  )
}
